import axios from 'axios';
import config from '../../../config/config.json';

// Interface for Livestream data
export interface Livestream {
  id?: string;
  name: string;
  url: string;
  description: string;
  published: boolean;
  discord_id: string;
  created_at?: string;
  updated_at?: string;
  team_name?: string;
}

export interface Discord {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  logo_url?: string;
}

// Verify authorization token exists
const verifyToken = () => {
  const token = localStorage.getItem('session_token');
  if (!token) {
    throw new Error('Authentication required');
  }
};

// Get headers with session token
export const getHeaders = () => {
  const sessionToken = localStorage.getItem('session_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (sessionToken) {
    headers['session-token'] = sessionToken;
  }

  return headers;
};

/**
 * Fetch all livestreams
 */
export async function getAllLivestreams(): Promise<Livestream[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/livestreams`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch a specific livestream by ID
 */
export async function getLivestream(id: string): Promise<Livestream> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/livestream/${id}`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Create a new livestream
 */
export async function createLivestream(livestream: Livestream): Promise<Livestream> {
  verifyToken();
  const response = await axios.post(`${config.backend_base_url}api/livestream`, livestream, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Update an existing livestream
 */
export async function updateLivestream(id: string, livestream: Livestream): Promise<Livestream> {
  verifyToken();
  const response = await axios.put(`${config.backend_base_url}api/livestream/${id}`, livestream, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Delete a livestream
 */
export async function deleteLivestream(id: string): Promise<void> {
  verifyToken();
  await axios.delete(`${config.backend_base_url}api/livestream/${id}`, {
    headers: getHeaders(),
  });
}

/**
 * Fetch all livestreams for a specific team
 */
export async function getTeamLivestreams(teamId: string): Promise<Livestream[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/team/${teamId}/livestreams`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch all Discord teams
 */
export async function getAllDiscords(): Promise<Discord[]> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/teams`, {
    headers: getHeaders(),
  });
  return response.data;
}

/**
 * Fetch a specific Discord team by ID
 */
export async function getDiscord(id: string): Promise<Discord> {
  verifyToken();
  const response = await axios.get(`${config.backend_base_url}api/discord/${id}`, {
    headers: getHeaders(),
  });
  return response.data;
}
